import styled from 'styled-components'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'semantic-ui-react'
import { milkSamplesIDs } from '~/Constants'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery'
import Layout from '~/components/Layout'
import { El } from '~/components/Layout/style'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'

const getDefaultTableComunHeaders = () => [
  {
    name: i18n.t('Date'),
    key: 'date',
    type: 'date',
    format: 'DD.MM.YYYY',
    sorted: 'desc'
  },
  {
    name: i18n.t('Fat (in %)'),
    key: 'fat',
    type: 'no-unit-number',
    sorted: 'desc'
  },
  {
    name: i18n.t('Protein (in %)'),
    key: 'protein',
    type: 'no-unit-number',
    sorted: 'desc'
  },
  {
    name: i18n.t('Germ count (1000/ml)'),
    key: 'germCount',
    type: 'no-unit-number',
    decimalScale: 0,
    sorted: 'desc'
  },
  {
    name: i18n.t('Cell count (1000/ml)'),
    key: 'cellCount',
    type: 'no-unit-number',
    decimalScale: 0,
    sorted: 'desc'
  },
  {
    name: i18n.t('Freezing point (in °C)'),
    key: 'freezingPoint',
    type: 'no-unit-number-no-value',
    sorted: 'desc'
  },
  {
    name: i18n.t('Inhibitor'),
    key: 'inhibitorLabel'
  },
  {
    name: i18n.t('Lactose (in %)'),
    key: 'lactose',
    type: 'no-unit-number-no-value',
    format: 'percent',
    sorted: 'desc'
  },
  {
    name: i18n.t('Fat-free dry matter (in %)'),
    key: 'fatFreeDryMatter',
    type: 'no-unit-number-no-value',
    sorted: 'desc'
  },
  {
    name: i18n.t('Free fatty acid'),
    type: 'no-unit-number-no-value',
    key: 'freeFattyAcid',
    sorted: 'desc'
  }
]

const defaultDataFilter = {
  page: 0,
  rows: 20,
  search: '',
  column: '',
  sort: '',
  date: '',
  germCountFrom: '',
  germCountTo: '',
  cellCountFrom: '',
  cellCountTo: '',
  inhibitor: ''
}

export const FilterWrapper = styled.div`
display: flex;
align-items: baseline;
flex-wrap: wrap;
gap: 10px;
`

const MilkSamples: React.FC = () => {
  const { t } = useTranslation()
  const [dataFiltered, setDataFiltered] = useState(defaultDataFilter)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableComunHeaders())

  const {
    isLoading: isBusyData,
    data: milkSamplesData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getMilkSamplesData(milkSamplesIDs.dailyValues, dataFiltered);

  const {
    isLoading: isBusyOptions,
    refetch: fetchOptions,
    data: milkSamplesOptions
  } = MilkMoneyQuery.getMilkSamplesFilterData(milkSamplesIDs.dailyValues)

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableComunHeaders())
    fetchOptions()
  }, [t])

  useEffect(() => {
    fetchFilteredData()
  }, [dataFiltered]);

  function renderActions() {
    return <El.PageHeaderActionButton>
      {t('Excel Export')}
    </El.PageHeaderActionButton>
  }

  function renderFilters() {
    return <FilterWrapper>
      <Dropdown
        selectOnBlur={false}
        placeholder={t('Delivery month tour')}
        selection
        value={dataFiltered.date}
        clearable
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, date: data.value as string })}
        options={milkSamplesOptions?.months?.map(option => ({ text: dayjs(option.label).format('MMM YYYY'), value: option.key }))}
      />
    </FilterWrapper>
  }

  function renderTable() {
    return <TableComponent
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  const { filtered, rows } = milkSamplesData ? milkSamplesData : { filtered: 0, rows: [] };

  return <Layout
    title={t('Milk samples')}
    filters={milkSamplesOptions && renderFilters()}
    actions={renderActions()}
    isBusy={isBusyData || isBusyOptions}
    segmented
  >
    {renderTable()}
  </Layout>
}
export default MilkSamples